import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CalendarEvent, EVENT_TYPE_CONFIG, useDeleteCalendarEvent } from "@/hooks/useCalendarEvents";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { format } from "date-fns";
import { ExternalLink, Lock, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  event: CalendarEvent | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onEdit: (ev: CalendarEvent) => void;
}

export const EventPreviewDialog = ({ event, open, onOpenChange, onEdit }: Props) => {
  const navigate = useTabNavigate();
  const deleteEvent = useDeleteCalendarEvent();
  if (!event) return null;

  const handleDelete = async () => {
    if (!confirm(`Delete "${event.title}"? This cannot be undone.`)) return;
    try {
      await deleteEvent.mutateAsync(event.id);
      toast.success("Event deleted");
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Failed to delete event");
    }
  };

  const cfg = EVENT_TYPE_CONFIG[event.event_type];

  const dateRange =
    event.end_date && event.end_date !== event.start_date
      ? `${format(new Date(event.start_date + "T00:00:00"), "MMM d")} – ${format(new Date(event.end_date + "T00:00:00"), "MMM d, yyyy")}`
      : format(new Date(event.start_date + "T00:00:00"), "MMM d, yyyy");

  const timeRange =
    !event.all_day && event.start_time
      ? `${event.start_time.slice(0, 5)}${event.end_time ? ` – ${event.end_time.slice(0, 5)}` : ""}`
      : "All day";

  const hasLinkedRecord = event.linked_wo_id || event.linked_draw_id || event.linked_invoice_id;

  const goToRecord = () => {
    if (event.linked_wo_id && event.project_id) {
      navigate(`/controller/projects/${event.project_id}`);
    } else if (event.linked_invoice_id) {
      navigate("/controller/invoices");
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: cfg.color }} />
            {event.title}
            {event.is_auto_generated && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <Row label="Type" value={cfg.label} />
          <Row label="Date" value={dateRange} />
          <Row label="Time" value={timeRange} />
          <Row label="Visibility" value={event.visibility} />
          {event.project_name && <Row label="Project" value={event.project_name} />}
          {event.description && (
            <div>
              <span className="text-xs text-muted-foreground">Notes</span>
              <p className="text-sm mt-0.5">{event.description}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex gap-2">
            {!event.is_auto_generated && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => { onOpenChange(false); onEdit(event); }}
                >
                  <Pencil className="h-3 w-3" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 text-destructive hover:text-destructive"
                  onClick={handleDelete}
                  disabled={deleteEvent.isPending}
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </Button>
              </>
            )}
            {hasLinkedRecord && (
              <Button variant="ghost" size="sm" className="gap-1" onClick={goToRecord}>
                Go to Record <ExternalLink className="h-3 w-3" />
              </Button>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between text-xs">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium capitalize">{value}</span>
  </div>
);
